// src/services/schemasSlice.ts

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiClient } from '@center-technologies/shared-api-clients';
import { UseCaseRow } from 'types/schema';

interface FetchUseCasesArgs {
  locale?: string;
  apiClient: ApiClient;
}

// Thunk: fetch use-cases (dispatched lazily by useUseCases hook, not at app launch)
export const fetchUseCases = createAsyncThunk<
  UseCaseRow[],
  FetchUseCasesArgs,
  { rejectValue: string }
>(
  'schemas/fetchUseCases',
  async ({ locale, apiClient }, thunkAPI) => {
    try {
      const params = new URLSearchParams();
      if (locale)         params.append('locale', locale);
      const query = params.toString() ? `?${params.toString()}` : '';
      return await apiClient.get<UseCaseRow[]>(`/use-cases${query}`);
    } catch (err: any) {
      console.error('Error fetching use cases:', err);
      return thunkAPI.rejectWithValue(err.message ?? 'Unknown error');
    }
  }
);

interface SchemasState {
  useCases: UseCaseRow[];
  useCasesStatus: 'idle' | 'loading' | 'succeeded' | 'failed';
  useCasesError: string | null;
}

const initialState: SchemasState = {
  useCases: [],
  useCasesStatus: 'idle',
  useCasesError: null,
};

const schemasSlice = createSlice({
  name: 'schemas',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUseCases.pending, (state) => {
        state.useCasesStatus = 'loading';
        state.useCasesError = null;
      })
      .addCase(
        fetchUseCases.fulfilled,
        (state, action: PayloadAction<UseCaseRow[]>) => {
          state.useCasesStatus = 'succeeded';
          state.useCases = action.payload;
        }
      )
      .addCase(fetchUseCases.rejected, (state, action) => {
        state.useCasesStatus = 'failed';
        state.useCasesError = action.payload ?? action.error.message ?? null;
      });
  },
});

export default schemasSlice.reducer;
